export interface UserAccessProfile {
  hasProjectId1: boolean;
  hasProjectId2: boolean;
  hasPmAccess: boolean;
  /** Native PM user (project_id = 2 only) — PM menu only, no Store360, no toggle. */
  isPmOnlyUser: boolean;
  /** Store360 user (project_id = 1) with PM toggle — Store360 + optional PM section. */
  isDualAccessUser: boolean;
  /** Roles in both projects — toggle between Store360 and PM menus. */
  isHybridUser: boolean;
  /** Show IntElux ↔ PM toggle in the header. */
  showProjectToggle: boolean;
}

/** Store360 / IntElux: projectId 1, or legacy null/0 in DB. */
function isStore360Role(role: { projectId?: number | null }): boolean {
  if (role.projectId == null) {
    return true;
  }
  const projectId = Number(role.projectId);
  return projectId === 0 || projectId === 1;
}

export function buildUserAccessProfile(
  roles: Array<{ projectId?: number | null }> | undefined,
  hasPmAccess: boolean,
): UserAccessProfile {
  const roleList = roles ?? [];
  const hasProjectId1 = roleList.some((role) => isStore360Role(role));
  const hasProjectId2 = roleList.some((role) => Number(role.projectId) === 2);
  // Native PM-only: projectId 2 roles and no Store360. Note: native PM users
  // also get a project_management_user row, but that alone is not "PM-only".
  const isPmOnlyUser = hasProjectId2 && !hasProjectId1;
  const isHybridUser = hasProjectId1 && hasProjectId2;
  // Toggle PM: in project_management_user without a native projectId=2 role
  // ⇒ IntElux + PM dual access (legacy project switcher / toggle behaviour).
  const isDualAccessUser = hasPmAccess && !hasProjectId2;
  const showProjectToggle =
    !isPmOnlyUser &&
    (hasProjectId1 || isDualAccessUser) &&
    (hasPmAccess || hasProjectId2);

  return {
    hasProjectId1: hasProjectId1 || isDualAccessUser,
    hasProjectId2,
    hasPmAccess,
    isPmOnlyUser,
    isDualAccessUser,
    isHybridUser,
    showProjectToggle,
  };
}

/** Primary Store360 role (project_id = 1 or legacy null/0) when present.
 * When a user has multiple IntElux roles, prefer Admin / Super BA Admin over
 * lower roles so Menu Setting for those roles is what they actually get.
 */
export function getStore360RoleId(
  roles: Array<{ id?: number; projectId?: number | null }> | undefined,
): number | undefined {
  const store360Roles = (roles ?? []).filter((role) => isStore360Role(role));
  if (!store360Roles.length) {
    return undefined;
  }
  const priority = [1, 23, 2, 3, 5, 4, 6]; // Admin, Super BA Admin, BA Admin, ...
  for (const id of priority) {
    const match = store360Roles.find((role) => Number(role.id) === id);
    if (match?.id != null) {
      return Number(match.id);
    }
  }
  return store360Roles[0]?.id != null ? Number(store360Roles[0].id) : undefined;
}

/** Primary native PM role (project_id = 2) when present. */
export function getNativePmRoleId(
  roles: Array<{ id?: number; projectId?: number }> | undefined,
): number | undefined {
  const pmRole = (roles ?? []).find((role) => Number(role.projectId) === 2);
  return pmRole?.id;
}
